import React from 'react';
import { useGetProcurementOrdersQuery } from '../../store/endpoints/ProcurementEndpoints';
import { IconButton } from '@fluentui/react';
import { PROCUREMENT_ORDER_STATUSES } from '../../Constants';
import { IProcurementOrder } from '../../models/Procurement';
import { useGetAllVendorSettingsQuery } from '../../store/Api';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import GridComposer from '../../components/GridComposer/GridComposer';
import { GridSelectionMode, IColumnDetails } from '../../models/GridConfiguration';
import ExistingOrderPanel from '../../components/Procurement/Order/ExistingOrderPanel';

// Helper to parse query string into filters object
const getFiltersFromQueryString = () => {
    const params = new URLSearchParams(window.location.search);
    const filtersObj: { [key: string]: string } = {};
    for (const [key, value] of params.entries()) {
        if (value !== undefined && value !== null) {
            filtersObj[key] = value;
        }
    }
    return filtersObj;
};

// Helper to update query string from filters object
const setQueryStringFromFilters = (filtersObj: { [key: string]: string }) => {
    const params = new URLSearchParams();
    Object.entries(filtersObj).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            params.set(key, value);
        }
    });
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState({}, '', newUrl);
};

const ProcurementOrdersPage: React.FC = () => {
    const [defaultCatalog, setDefaultCatalog] = React.useState<string>();
    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const [page, setPage] = React.useState(1);
    const vendorSettings = useGetAllVendorSettingsQuery();
    const [sortBy, setSortBy] = React.useState<string>('lastUpdated');
    const [sortDirection, setSortDirection] = React.useState<string>('desc');
    const [pageSize, setPageSize] = React.useState<number>(50);
    const [selectedOrderId, setSelectedOrderId] = React.useState<string | undefined>(undefined);
    const [filters, setFilters] = React.useState<{ [key: string]: string }>(() => getFiltersFromQueryString());


    // Update query string when filters change
    React.useEffect(() => {
        setQueryStringFromFilters(filters);
    }, [filters]);

    // On mount, load filters from query string (in case of browser navigation)
    React.useEffect(() => {
        setFilters(getFiltersFromQueryString());
        // Listen for popstate (back/forward navigation)
        const onPopState = () => {
            setFilters(getFiltersFromQueryString());
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    const orders = useGetProcurementOrdersQuery({
        conditions: {
            page: page,
            pageSize: pageSize,
            sortBy: sortBy,
            sortDirection: sortDirection,
            filters: filters,
        }
    })

    const closePanel = () => {
        setSelectedOrderId(undefined);
        orders.refetch();
    }


    const getCustomColumns = (): IColumnDetails[] => ([
        {
            // index: 0,
            identifier: 'Details',
            label: '',
            type: '',
            component: (order: IProcurementOrder) => <IconButton iconProps={{ iconName: 'EntryView' }} onClick={() => setSelectedOrderId(order.id)} />,
            required: true,
        },
        {
            identifier: 'status.keyword',
            label: 'Status',
            type: 'Text',
            minimumWidth: 100,
            columnDefault: true,
            sortable: true,
            filterable: true,
            customFilterOptions: () => Object.entries(PROCUREMENT_ORDER_STATUSES).map(([key, value]) => ({ key: value, text: value })),
        }
    ])
    //TODO: Vendor options

    return (
        <div className='main-content'>
            <h1>Procurement Orders</h1>
            <GridComposer
                columnSetName='ProcurementOrder'
                initialPageSize={50}
                data={orders.data ?? {}}
                page={page}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                selectionMode={GridSelectionMode.none}
                loading={orders.isLoading || orders.isFetching}
                onRefresh={() => orders.refetch()}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={(newSortBy, newSortDirection) => {
                    setSortBy(newSortBy ?? 'lastUpdated');
                    setSortDirection(newSortDirection ?? 'desc');
                }}
                canChangePageSize={false}
                canCreateCsv={false}
                canCustomizeColumns
                canResizeColumns
                canDragColumns
                overrideColumns={getCustomColumns()}
                filters={filters}
                onFiltersChange={setFilters}
                showFiltersAsTags
                getItemId={(item: IProcurementOrder) => item.id}
                />
            <ExistingOrderPanel orderId={selectedOrderId} onClose={closePanel} />
        </div>
    );
}

export default ProcurementOrdersPage;